import { useState } from "react";
import { Button, Card, Icon, Text, useToast } from "@app/ui";
import { downloadPassportQr } from "../api/dpp.js";
import { QrSvg } from "../components/PassportQr.js";
import { useT } from "../i18n.js";
import type { PassportDetail } from "../types.js";

/** The public page link + QR, or a placeholder until the passport is published. */
export function PassportPublicAccessCard({
  orgId,
  passport,
  publicUrl,
  openLabel,
  onError,
}: {
  orgId: string;
  passport: PassportDetail;
  publicUrl: string;
  openLabel: string;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);

  const downloadQr = async () => {
    setDownloading(true);
    try {
      const blob = await downloadPassportQr(orgId, passport.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Must end in .svg so the OS opens it with an image viewer; mirrors the
      // API's own content-disposition filename.
      a.download = `passport-${passport.publicSlug}.qr.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e);
    } finally {
      setDownloading(false);
    }
  };

  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("passport.linkCopied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  return (
    <Card title={t("passport.publicAccess")}>
      {passport.status === "published" ? (
        <div className="flex flex-col items-center gap-3">
          <QrSvg text={publicUrl} size={168} className="rounded-lg border border-border" />
          <Text small secondary>
            {t("passports.qrHint")}
          </Text>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={publicUrl}
            aria-label={`${t("passport.publicUrl")}: ${openLabel}`}
            className="max-w-full truncate font-mono text-xs text-primary underline-offset-2 hover:underline"
          >
            {publicUrl}
          </a>
          <div className="flex w-full flex-wrap gap-2">
            <Button
              size="sm"
              className="flex-1"
              icon={<Icon name="copy" size={14} />}
              onClick={() => void copyPublicUrl()}
            >
              {t("passport.copyLink")}
            </Button>
            <Button
              size="sm"
              className="flex-1"
              loading={downloading}
              icon={<Icon name="download" size={14} />}
              onClick={() => void downloadQr()}
            >
              {t("passport.qr")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon name="eye" size={20} />
          </span>
          <Text small secondary>
            {t("passport.notPublicYet")}
          </Text>
        </div>
      )}
    </Card>
  );
}
